import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import Header from "./components/Header/Header";
import { Container } from "./components/styled/styled-components";
import TokenChecker from "./components/TokenChecker/TokenChecker";
import ContextApp from "./context/ContextApp";
import { useDocumentTitle } from "./hooks/document-title";
import AuthPage from "./pages/auth/AuthPage";
import BranchSelection from "./pages/branch-selection/BranchSelection";
import CartPage from "./pages/cart/CartPage";
import ExtraInfo from "./pages/check-out/extra-info/ExtraInfo";
import OrderReview from "./pages/check-out/order-review/OrderReview";
import BaseShippingPage from "./pages/check-out/shipping-selection/BaseShippingPage";
import NotFound from "./pages/not-found/NotFound";
import OrderHistory from "./pages/order-history/OrderHistory";
import OrderUploader from "./pages/order-uploader/OrderUploader";
import Preferences from "./pages/preferences/Preferences";
import ProductDetail from "./pages/product/ProductDetail";
import ProductList from "./pages/products/ProductList";
import QuickOrder from "./pages/quick-order/QuickOrder";
import SavedOrders from "./pages/saved-orders/SavedOrders";
import Search from "./pages/search/Search";
import Transactions from "./pages/transactions/Transactions";
import { getSubdomain } from "./context/ContextApp";
import { setIsWebstoreAdmin, setOrganizationKey } from "./redux/actions/organization";
import { setIsWebstoreOrderEnabled, setLoginStatus } from "./redux/actions/userSession";
import { RootState } from "./redux/store";
import "./styles/index.scss";
import PaymentSelection from "./pages/check-out/payment-selection/PaymentSelection";
import MaintenanceMode from "./MaintencanceMode";
import HomePage from "./pages/home-page/HomePage";
import IsoCertificationPage from "./pages/iso-certification/IsoCertification";
import PaymentForm from "./pages/payment-form/PaymentForm";
import { injectContentsquareScript } from "@contentsquare/tag-sdk";

const App = () => {
  const isAuthenticated = useSelector((state: RootState) => state.userSession.isAuthenticated);
  const [isTokenChecked, setIsTokenChecked] = useState(false);
  const dispatch = useDispatch();
  const appBranch = getSubdomain();
  const isWebstoreOrderEnable = localStorage.getItem("webstore_order_enabled") === "true";

  const maintenanceBranches = process.env.REACT_APP_ENABLE_MAINTENANCE_MODE_FOR_BRANCHES;
  const isMaintenanceMode =
    !!maintenanceBranches && maintenanceBranches.split(",").includes(appBranch ?? "");

  useDocumentTitle(`Fashion Biz Webstore${appBranch ? ` ${appBranch.toUpperCase()}` : ""}`);

  // Hotjar Script for Analytics
  injectContentsquareScript({
    siteId: process.env.REACT_APP_HOTJAR_SITE_ID,
    async: true, // Optional: Set to false to wait for script execution until after document parsing.
    defer: false, // Optional: Set to true to defer script execution after document parsing.
  });

  useEffect(() => {
    if (self == top) {
      // Everything checks out, show the page.
      document.documentElement.style.display = "block";
    } else {
      // Break out of the frame.
      if (top != undefined) {
        (top as any).location = self.location;
      }
    }
    try {
      const localStorageWebStoreAdmin = localStorage.getItem("webstore_admin");
      if (localStorageWebStoreAdmin) {
        dispatch(setIsWebstoreAdmin(JSON.parse(localStorageWebStoreAdmin)));
      }

      const localStorageOrganizationKey = localStorage.getItem("organization_key");
      if (localStorageOrganizationKey) {
        if (localStorageOrganizationKey === "null") {
          dispatch(setOrganizationKey(JSON.parse(localStorageOrganizationKey)));
        }
        dispatch(setOrganizationKey(localStorageOrganizationKey));
      }

      const localStorageWebStoreOrderEnabled = localStorage.getItem("webstore_order_enabled");
      if (localStorageWebStoreOrderEnabled) {
        dispatch(setIsWebstoreOrderEnabled(JSON.parse(localStorageWebStoreOrderEnabled)));
      }
    } catch (error) {
      setLoginStatus(false);
    }
  }, [dispatch]);

  const setTokenCheckedIsTrue = useCallback(() => {
    setIsTokenChecked(true);
  }, []);

  if (isMaintenanceMode) {
    return <MaintenanceMode />;
  }

  return (
    <ContextApp>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <TokenChecker setIsTokenChecked={setTokenCheckedIsTrue} />
        {!isTokenChecked ? (
          <div className="waiting-page" />
        ) : !isAuthenticated ? (
          <React.Fragment>
            <Routes>
              <Route path="/reset_password/:token?" element={<AuthPage reset />} />
              <Route path="/" element={<AuthPage />} />
            </Routes>
          </React.Fragment>
        ) : (
          <div id="app">
            <Header />
            <Container row colmobile style={{ height: "100%" }}>
              <div className="app-main-content">
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/store/cart" element={<CartPage />} />
                  <Route path="/iso-certification" element={<IsoCertificationPage />} />
                  <Route path="/payment-form/:orderId" element={<PaymentForm />} />
                  <Route path="/products" element={<ProductList />} />
                  <Route path="/products/:brand" element={<ProductList />} />
                  <Route path="/products/:brand/:slug" element={<ProductDetail />} />
                  <Route path="/quick-order" element={<QuickOrder />} />
                  <Route path="/search" element={<Search />} />
                  {isWebstoreOrderEnable && (
                    <Route path="/check-out/extra-info" element={<ExtraInfo />} />
                  )}
                  <Route path="/check-out/shipping-selection" element={<BaseShippingPage />} />
                  <Route path="/check-out/payment-selection" element={<PaymentSelection />} />
                  <Route path="/check-out/order-review" element={<OrderReview />} />
                  <Route path="/saved-orders" element={<SavedOrders />} />
                  <Route path="/order-history" element={<OrderHistory />} />
                  <Route path="/branch-selection" element={<BranchSelection />} />
                  <Route path="/transactions" element={<Transactions />} />
                  <Route path="/preferences" element={<Preferences />} />
                  <Route path="/order-uploader" element={<OrderUploader />} />

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </div>
            </Container>
          </div>
        )}
      </Router>
    </ContextApp>
  );
};

export default App;
